import { find } from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import {
  matchPath,
  Redirect,
  Route,
  RouteComponentProps,
  Switch
} from "react-router-dom";
import NavLink from "../../../../components/NavLink";
import { useHotspots } from "../../../../context/hotspots";
import useMapConfig, {
  WitnessesVisualizationKey
} from "../../../../utils/useMapConfig";
import NeighborsTooCloseToWitness from "../../common/NeighborsTooCloseToWitness";
import BidirectionalWitnessesTab from "./BidirectionalWitnessesTab";
import WitnessedByTab from "./WitnessedByTab";
import WitnessedOthersTab from "./WitnessedOthersTab";
import WitnessesTable from "./WitnessesTable";

const HotspotWitnessesTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const {
    currentHotspot,
    hotspotWitnessesCountsMap,
    isNotFetchedInLastMinute,
    fetchHotspotWitnessData,
    fetchWitnessesCountsByHotspots,
    lastFetched
  } = useHotspots();
  const witnessesVisualizationKey = useMapConfig(
    (state) => state.witnessesVisualizationKey
  );
  const witnessesVisualizationVariant = useMapConfig(
    (state) => state.witnessesVisualizationVariant
  );
  const setWitnessesVisualizationKey = useMapConfig(
    (state) => state.setWitnessesVisualizationKey
  );

  const onChangeTab = useCallback(
    (key) => {
      const tab = find(tabs, { key });
      if (!tab) return;
      if (
        witnessesVisualizationVariant &&
        [
          WitnessesVisualizationKey.witnessedBy,
          WitnessesVisualizationKey.witnessedOthers,
          WitnessesVisualizationKey.bidirectional
        ].includes(witnessesVisualizationKey)
      ) {
        setWitnessesVisualizationKey(tab.visualisationKey);
      }
    },
    [
      setWitnessesVisualizationKey,
      witnessesVisualizationKey,
      witnessesVisualizationVariant
    ]
  );

  const detailsPageMatch = matchPath<{
    id: string;
    subsection: string;
  }>(props.location.pathname, {
    path: "/hotspots/:id/witnesses/:subsection"
  });

  const isLoading = useMemo(
    () => !(currentHotspot && hotspotWitnessesCountsMap[currentHotspot?.id]),
    [currentHotspot, hotspotWitnessesCountsMap]
  );

  useEffect(() => {
    if (!currentHotspot) return;

    const now = Date.now().valueOf();

    const witnessDataFetchId = `hotspot-witness-data-${currentHotspot?.id}`;
    if (isNotFetchedInLastMinute(witnessDataFetchId)) {
      fetchHotspotWitnessData(currentHotspot.id);
      lastFetched.current[witnessDataFetchId] = now;
    }

    const witnessesCountsFetchId = `hotspot-witnesses-counts-${currentHotspot.id}`;
    if (isNotFetchedInLastMinute(witnessesCountsFetchId)) {
      fetchWitnessesCountsByHotspots([currentHotspot.id]);
      lastFetched.current[witnessesCountsFetchId] = now;
    }
  }, [
    currentHotspot,
    fetchHotspotWitnessData,
    fetchWitnessesCountsByHotspots,
    isNotFetchedInLastMinute,
    lastFetched
  ]);

  if (!currentHotspot) return null;

  return (
    <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <NeighborsTooCloseToWitness
        className="mb-6"
        id={currentHotspot.id}
        h3Index={currentHotspot.location}
      />

      {!isLoading && (
        <div className="mb-6">
          <select
            className="sm:hidden block w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800"
            onChange={(e) => {
              props.history.push(`${props.match.url}/${e.target.value}`);
              onChangeTab(e.target.value);
            }}
            value={detailsPageMatch?.params.subsection}
          >
            {tabs.map(({ key, title }) => (
              <option key={key} value={key}>
                {title}
              </option>
            ))}
          </select>
          <div className="hidden sm:block">
            <nav className="flex space-x-4" aria-label="Tabs">
              {tabs.map(({ key, title }) => (
                <NavLink
                  key={key}
                  to={`${props.match.url}/${key}`}
                  activeClassName="bg-hotspotty-100 text-hotspotty-700 dark:text-white dark:bg-gray-700 dark:text-white"
                  inactiveClassName="text-gray-500 hover:text-gray-700 hover:bg-gray-50 group dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-transparent"
                  className="px-3 py-2 font-medium text-sm rounded-md"
                  onClick={() => {
                    onChangeTab(key);
                  }}
                >
                  {title}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}

      <Switch>
        <Route
          path={`${props.match.path}/witnessed-others`}
          component={WitnessedOthersTab}
        />
        <Route
          path={`${props.match.path}/witnessed-by`}
          component={WitnessedByTab}
        />
        <Route
          path={`${props.match.path}/bidirectional`}
          component={BidirectionalWitnessesTab}
        />
        <Redirect to={`${props.match.path}/witnessed-others`} />
      </Switch>

      <div className="mt-6">
        <WitnessesTable />
      </div>
    </div>
  );
};

export default HotspotWitnessesTab;

//
// Utils
//

const tabs = [
  {
    key: "witnessed-others",
    title: "As witness",
    visualisationKey: WitnessesVisualizationKey.witnessedOthers
  },
  {
    key: "witnessed-by",
    title: "As transmitter",
    visualisationKey: WitnessesVisualizationKey.witnessedBy
  },
  {
    key: "bidirectional",
    title: "Bidirectional",
    visualisationKey: WitnessesVisualizationKey.bidirectional
  }
];

export const DEFAULT_DAYS_OF_WITNESSES_DATA = 7;
